import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/http';

export interface FeeComponent {
  label: string;
  kind: 'PERCENT' | 'FIXED';
  /** PERCENT: fração ("0.2"); FIXED: reais ("4.50"). */
  value: string;
}

export interface FeeBand {
  minPrice: string;
  maxPrice: string | null;
  components: FeeComponent[];
}

export interface FeeTable {
  id: string;
  provider: string;
  version: number;
  effectiveFrom: string;
  bands: FeeBand[];
  sellerShipping: string | null;
  sourceUrl: string;
  evidence: string;
  approvedAt: string;
}

export type FeeTableInput = Omit<FeeTable, 'id' | 'version' | 'approvedAt'>;

const key = ['platform', 'fee-tables'] as const;

/** Tabelas de tarifa aprovadas (ADR-077) e as plataformas que precisam delas. */
export function useFeeTables() {
  return useQuery({
    queryKey: key,
    queryFn: () =>
      apiRequest<{ providers: { provider: string; displayName: string }[]; tables: FeeTable[] }>(
        '/platform/fee-tables',
      ),
  });
}

export function useCreateFeeTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FeeTableInput) =>
      apiRequest<FeeTable>('/platform/fee-tables', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

/** Uma leitura automática dos documentos oficiais (ADR-085). */
export interface FeeTableProposal {
  id: string;
  provider: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'UNCHANGED' | 'FAILED' | 'SUPERSEDED';
  proposed: FeeTableInput | null;
  baseVersion: number | null;
  changes: string[];
  notes: string[];
  error: string | null;
  documents: { name: string; url: string; images: string[] }[];
  model: string | null;
  costUsd: string;
  createdAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
  approvedVersion: number | null;
}

export interface FeeTableWatch {
  provider: string;
  displayName: string;
  sources: { name: string; url: string }[];
  lastCheckedAt: string | null;
  lastChangedAt: string | null;
  lastError: string | null;
  open: FeeTableProposal | null;
  history: FeeTableProposal[];
}

const readingKey = ['platform', 'fee-tables', 'reading'] as const;

/**
 * Leitura automática de cada plataforma. Depois de "Ler agora", a tela volta a perguntar a cada
 * 15 s: a leitura com figura roda no worker e leva até dois minutos.
 */
export function useFeeTableReading(polling: boolean) {
  return useQuery({
    queryKey: readingKey,
    queryFn: () => apiRequest<FeeTableWatch[]>('/platform/fee-tables/reading'),
    refetchInterval: polling ? 15_000 : false,
  });
}

/** Leituras esperando decisão: o aviso fixo do painel. */
export function usePendingFeeTableReadings() {
  return useQuery({
    queryKey: [...readingKey, 'pending'],
    queryFn: () => apiRequest<{ pending: number }>('/platform/fee-tables/reading/pending'),
  });
}

export function useRequestFeeTableReading() {
  return useMutation({
    mutationFn: (provider: string) =>
      apiRequest<void>(`/platform/fee-tables/reading/${provider}/check`, { method: 'POST' }),
  });
}

function useInvalidateFeeTables() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: key });
}

/** Aprova a leitura como está (`table` nulo) ou corrigida pela pessoa. */
export function useApproveFeeTableProposal() {
  const invalidate = useInvalidateFeeTables();
  return useMutation({
    mutationFn: ({ id, table }: { id: string; table: FeeTableInput | null }) =>
      apiRequest<FeeTable>(`/platform/fee-tables/proposals/${id}/approve`, {
        method: 'POST',
        body: { table },
      }),
    onSuccess: invalidate,
  });
}

export function useRejectFeeTableProposal() {
  const invalidate = useInvalidateFeeTables();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiRequest<void>(`/platform/fee-tables/proposals/${id}/reject`, {
        method: 'POST',
        body: { note },
      }),
    onSuccess: invalidate,
  });
}
